import { Link } from "react-router-dom";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { PricingTable } from "../components/PricingTable";
import {
  Sparkles, Zap, Search, Users, Film, ArrowRight, Check,
  Cloud, Lock, Layers, Rocket, ChevronDown,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const HERO_BG = "https://images.unsplash.com/photo-1471341971476-ae15ff5dd4ea?w=2200&q=80";
const COLLAB = "https://images.unsplash.com/photo-1752650733548-ca10d0d69f06?w=1600&q=80";
const VIDEO_TILE_1 = "https://images.unsplash.com/photo-1699446948863-4879e5f97403?w=900&q=70";
const VIDEO_TILE_2 = "https://images.unsplash.com/photo-1698940273205-2b903607df31?w=900&q=70";
const VIDEO_TILE_3 = "https://images.unsplash.com/photo-1718073398740-9d3484bd6340?w=900&q=70";

const goLogin = () => {
  window.location.href = "/login";
};

const Marquee = () => (
  <div className="border-y border-black/15 bg-black text-white overflow-hidden">
    <div className="marquee-track py-4">
      {Array(6).fill(0).map((_, i) => (
        <div key={i} className="flex items-center gap-12 px-6 shrink-0">
          {["INTELLIGENT MEDIA", "FASTER SYNC", "200+ FILE TYPES", "NATURAL LANGUAGE SEARCH", "BUILT FOR SQUADS", "FRACTION OF THE PRICE"].map((t) => (
            <span key={t} className="font-heading font-black tracking-tight text-2xl whitespace-nowrap">
              {t} <span className="text-[#E60000]">●</span>
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

export default function Landing() {
  const { user } = useAuth();

  return (
    <div className="bg-white text-black">
      {/* Top bar */}
      <header className="sticky top-0 z-40 glass-light">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-8 text-sm tracking-tight">
            <a href="#features" className="hover:text-[#E60000]" data-testid="link-features">Features</a>
            <a href="#compare" className="hover:text-[#E60000]" data-testid="link-compare">Compare</a>
            <a href="#pricing" className="hover:text-[#E60000]" data-testid="link-pricing">Pricing</a>
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <Link to="/library">
                <Button className="rounded-sm bg-[#E60000] hover:bg-[#CC0000] text-white" data-testid="enter-app">
                  Enter app <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            ) : (
              <>
                <Button variant="ghost" onClick={goLogin} className="rounded-sm" data-testid="signin-top">Sign in</Button>
                <Button onClick={goLogin} className="rounded-sm bg-[#E60000] hover:bg-[#CC0000] text-white" data-testid="cta-top">
                  Get squad access
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative bg-black text-white grain overflow-hidden">
        <div
          className="absolute inset-0 opacity-30 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-black" />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 py-20 md:py-32">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-bold text-[#E60000] mb-6">
            <span className="w-8 h-px bg-[#E60000]" /> The Monster Squad NAS · v01
          </div>
          <h1 className="font-display text-[14vw] md:text-[8.5rem] lg:text-[10rem] font-black leading-[0.85] tracking-tighter">
            Storage<br />
            <span className="text-[#E60000]">that thinks</span><br />
            <span className="italic font-heading">in plain english.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg md:text-xl text-white/75 leading-relaxed">
            Monster Squad is the cloud NAS for creative teams. Share media instantly,
            search it like you'd ask a colleague, and ship faster — at a fraction of
            what Shade, Dropbox, or Frame.io charges.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Button onClick={goLogin} size="lg" className="h-14 px-8 rounded-sm bg-[#E60000] hover:bg-[#CC0000] text-white text-base font-medium tracking-tight" data-testid="hero-cta">
              Start free <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
              variant="outline" size="lg"
              className="h-14 px-8 rounded-sm bg-transparent border-white/30 text-white hover:bg-white hover:text-black text-base">
              See how it works <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
            <div className="ml-2 flex items-center gap-3 text-white/60 text-sm">
              <Check className="w-4 h-4 text-[#E60000]" /> 5 GB free · no card
            </div>
          </div>

          {/* Floating preview tiles */}
          <div className="hidden lg:grid grid-cols-3 gap-3 mt-20 pointer-events-none">
            {[VIDEO_TILE_1, VIDEO_TILE_2, VIDEO_TILE_3].map((src, i) => (
              <div key={i} className="aspect-video bg-white/5 border border-white/10 overflow-hidden relative animate-fade-up" style={{ animationDelay: `${i * 120}ms` }}>
                <img src={src} alt="" className="w-full h-full object-cover opacity-90" />
                <span className="absolute top-2 left-2 font-mono text-[10px] tracking-wider px-1.5 py-0.5 bg-white text-black font-bold">VIDEO</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      {/* FEATURES */}
      <section id="features" className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="max-w-3xl">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E60000]">Features · 01—04</div>
          <h2 className="mt-3 font-display text-5xl md:text-7xl font-black tracking-tighter leading-[0.9]">
            A YouTube,<br />a TikTok, and an<br /> intelligent vault — <em className="font-heading font-black not-italic text-[#E60000]">in one.</em>
          </h2>
        </div>

        <div className="mt-16 grid md:grid-cols-2 gap-px bg-black/10">
          {[
            { icon: Search, title: "Ask, don't browse.", body: "Type 'sunset clips with people, last month' — Claude Sonnet 4.5 answers from your vault. No tags required." },
            { icon: Film, title: "Library & Reels in one.", body: "A YouTube-style grid for your archive. A vertical 9:16 short feed for the squad. Same source of truth." },
            { icon: Zap, title: "Faster sync, smaller bill.", body: "Up to 1 GB/s sync over Monster Squad CDN. A fraction of what Shade, Dropbox, or Frame.io charge." },
            { icon: Users, title: "Built for squads.", body: "Comments, shares, folders, view counts. Your producer, editor and client live on the same page." },
          ].map((f, i) => (
            <div key={i} className="bg-white p-10 group hover:bg-black hover:text-white transition-colors">
              <f.icon className="w-7 h-7 text-[#E60000]" strokeWidth={1.5} />
              <h3 className="mt-5 font-heading font-black text-2xl tracking-tight">{f.title}</h3>
              <p className="mt-3 text-base text-black/65 group-hover:text-white/65 leading-relaxed max-w-md">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* COLLABORATION */}
      <section className="bg-[#0A0A0A] text-white grain">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E60000]">Collaboration · 02</div>
            <h2 className="mt-3 font-display text-5xl md:text-6xl font-black tracking-tighter leading-[0.9]">
              Frame-perfect comments. Folder-level access. <span className="text-[#E60000]">Zero email.</span>
            </h2>
            <ul className="mt-8 space-y-4">
              {[
                "Drag-and-drop uploads with parallel sync",
                "200+ file types — from RAW to FCPXML",
                "Per-folder permissions for clients & freelancers",
                "Activity feed: who watched, who liked, who replied",
              ].map((t, i) => (
                <li key={i} className="flex gap-3">
                  <Check className="w-5 h-5 text-[#E60000] mt-0.5" />
                  <span className="text-white/80">{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative aspect-[4/3] overflow-hidden border border-white/10">
            <img src={COLLAB} alt="creative team collaborating" className="w-full h-full object-cover" />
            <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black to-transparent">
              <div className="font-mono text-[10px] tracking-wider text-[#E60000]">SQUAD · LIVE</div>
              <div className="font-heading font-bold text-lg">3 editors · 2 reviewers</div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARE */}
      <section id="compare" className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E60000]">Compare · 03</div>
        <h2 className="mt-3 font-display text-5xl md:text-6xl font-black tracking-tighter">
          More files. Faster sync.<br />
          <span className="text-[#E60000]">A fraction of the price.</span>
        </h2>
        <div className="mt-12">
          <PricingTable />
        </div>
      </section>

      {/* CTA */}
      <section id="pricing" className="bg-[#E60000] text-white">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
          <h2 className="font-display text-5xl md:text-7xl font-black tracking-tighter leading-[0.9] text-balance">
            Bring your media home.<br />Bring your squad with you.
          </h2>
          <p className="mt-6 text-white/85 max-w-xl mx-auto text-lg">
            5 GB free, forever. Upgrade when your archive does.
          </p>
          <Button onClick={goLogin} size="lg" className="mt-10 h-14 px-10 rounded-sm bg-black hover:bg-white hover:text-black text-white text-base font-medium tracking-tight" data-testid="cta-bottom">
            Sign in with Google <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <div className="mt-6 flex items-center justify-center gap-6 text-sm text-white/70">
            <span className="flex gap-2"><Lock className="w-4 h-4" /> SOC2 ready</span>
            <span className="flex gap-2"><Cloud className="w-4 h-4" /> Object storage</span>
            <span className="flex gap-2"><Layers className="w-4 h-4" /> 200+ formats</span>
            <span className="flex gap-2"><Rocket className="w-4 h-4" /> 1 GB/s sync</span>
          </div>
        </div>
      </section>

      <footer className="bg-black text-white">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-14 flex flex-col md:flex-row justify-between gap-6">
          <Logo inverted />
          <div className="text-sm text-white/50">
            © {new Date().getFullYear()} Monster Squad™. Built for the people who make the cut.
          </div>
        </div>
      </footer>
    </div>
  );
}
